import type { SceneMeta } from '../core/types';

interface Chapter { label: string; start: number; scenes: SceneMeta[] }

/** Consecutive scenes with the same chapter label form one group (e.g. the whole MINT family). */
function chapters(scenes: SceneMeta[]): Chapter[] {
  const list: Chapter[] = [];
  scenes.forEach((scene, index) => {
    const last = list[list.length - 1];
    if (last && last.label === scene.chapter) last.scenes.push(scene);
    else list.push({ label: scene.chapter, start: index, scenes: [scene] });
  });
  return list;
}

/**
 * ManualProgress (06-COMPONENTS-AND-STATES.md): chapters in running order, one tick per scene, and
 * a step meter for the active scene. Every tick is a real button; chrome stays neutral and only the
 * active marker takes the current brand accent.
 */
export class ProgressMap {
  public readonly element: HTMLElement;
  private readonly buttons: HTMLButtonElement[];
  private readonly groups: HTMLElement[];
  private readonly meter: HTMLElement;

  constructor(
    scenes: SceneMeta[],
    private readonly onJump: (index: number) => void,
  ) {
    this.element = document.createElement('nav');
    this.element.className = 'progress-map';
    this.element.setAttribute('aria-label', 'Presentation progress');
    this.element.innerHTML = `${chapters(scenes)
      .map((chapter) => `
        <div class="pm-chapter" data-chapter="${chapter.label}">
          <span class="pm-chapter__label">${chapter.label}</span>
          <div class="pm-ticks">
            ${chapter.scenes.map((scene, i) => {
              const index = chapter.start + i;
              return `<button type="button" data-scene="${index}" aria-label="Scene ${index + 1}: ${scene.title}" data-title="${String(index + 1).padStart(2, '0')} · ${scene.title}"><i></i></button>`;
            }).join('')}
          </div>
        </div>`)
      .join('')}
      <span class="pm-meter" aria-hidden="true"><i></i></span>`;

    this.buttons = Array.from(this.element.querySelectorAll<HTMLButtonElement>('button[data-scene]'));
    this.groups = Array.from(this.element.querySelectorAll<HTMLElement>('.pm-chapter'));
    this.meter = this.element.querySelector<HTMLElement>('.pm-meter i')!;

    this.element.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-scene]');
      if (!button) return;
      this.onJump(Number(button.dataset.scene));
    });
  }

  public update(activeIndex: number, step = 0, stepCount = 1): void {
    this.buttons.forEach((button, index) => {
      button.dataset.state = index === activeIndex ? 'active' : index < activeIndex ? 'complete' : 'idle';
      if (index === activeIndex) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    const active = this.buttons[activeIndex];
    this.groups.forEach((group) => { group.dataset.active = String(group.contains(active)); });
    this.meter.style.transform = `scaleX(${((step + 1) / stepCount).toFixed(3)})`;
  }
}
